import capitalize from 'lodash/capitalize';
import camelCase from 'lodash/camelCase';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import isObject from 'lodash/isObject';
import last from 'lodash/last';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import partition from 'lodash/partition';
import pick from 'lodash/pick';
import {AttrsSchema, Component} from 'panel';

import {prompt} from 'common/components/confirm';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {isPopulatedSection, itemHasSubSections} from 'common/components/items-menu/utils';
import {Visibility} from 'common/components/base/stackable';
import {MATH_TYPE, PRIMATIVE_MATH_TYPES, ProjectFeatureFlag, SPECIAL_EVENTS} from 'common/report/constants';
import {featuresStore} from 'common/report/stores/features';
import {dataGroupsClient} from 'common/report/queries/app-api/data-groups-client';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {maxQueryReason} from 'common/report/util/query-tracking';
import {renameProperty} from 'common/report/util/properties';
import {getAlphaLabelFromIndex, serializeAttributes} from 'common/util';
import {pluralize, toTitleCase} from 'common/util/string';
import {insertAtIndex, removeByIndex, replaceByIndex} from 'common/util/array';
import {emitTrackEvent, preventTracking, propagateEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {findKeyByValue} from 'common/util/typescript';
import {DEFAULT_DATA_GROUP} from 'common/widgets/data-group-selector';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DateRangeFactory} from 'common/widgets/date-range-picker/models/date-range-factory';
import {DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {PropertyType, ResourceType} from 'common/widgets/property-filter-menu/models/types';
import {TRACKING_EVENTS as QueryBlockTrackingEvent} from 'common/widgets/query-block/constants';
import {EVENT_PROPERTIES, EventItem} from 'common/widgets/query-block/events-menu/types';
import {PropertyItem} from 'common/widgets/query-block/properties-menu/types';
import {isSamePropertyReference, segmentationToPropertyItem} from 'common/widgets/query-block/properties-menu/util';
import {
  isCohortSegment,
  isEmptySegmentSelection,
  isEventSegment,
  isOtherSegment,
  isUserSegment,
  Segment,
} from 'common/widgets/query-block/segment-menu/types';
import {useNewMetricsMode} from 'common/widgets/query-builder/util';
import {RangeSelection} from 'common/widgets/step-range-selector';
import {
  getTempLookupTableMappings,
  lookupTablesStore,
  unregisterTempLookupTableMapping,
} from 'common/report/stores/lookup-tables';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {promisifyModal} from 'common/components/modal/utils';

import {Bucket} from '../bucket-pane/types';
import {Segmentation} from '../property-filter-menu/models/segfilter';
import {createFirstTimeEventFilter, isFirstTimeEventFilter} from '../property-filter-menu/models/utils';
import {PropertyFilter} from '../property-filter-menu/models/property-filter';
import {generateFilterTrackingProps, transformFilterToPropertyItem} from '../query-block/filter-menu/util';
import {propIneligibleForMappingReason} from '../map-lookup-table-modal/util';
import type {MapLookupTableModalAttrs, PropertyItemWithValue} from '../map-lookup-table-modal';
import {
  ControlBarAction,
  ControlBarActionItems,
  DefaultCustomBucket,
  FlattenSelectorTypeAggregations,
  nestedSelectorTypeAggregationsSections,
} from './constants';
import {template} from './template';
import css from './index.styl?inline';
import {ResourceTrackingEvent, ResourceTrackingTypes, TrackingEvent} from './tracking';
import {
  Action,
  AggregationGroupLabel,
  canPerformPropertyAggregation,
  EMPTY_EVENT_ENTRY,
  EMPTY_EXCLUSION_EVENT_ENTRY,
  EntryItem,
  EntryPart,
  EntrySectionUpdateEvent,
  EntryType,
  GroupEventBehaviors,
  isEventBasedType,
  isFilterEntry,
  isGroupedEntry,
  isInlineCohortItem,
  isPropertyEntry,
  isPropertySelectorType,
  isSegmentEntry,
  isUserBasedType,
  QueryBucketableEntry,
  QueryEntry,
  QueryEventEntry,
  QueryGroupedEntry,
  QueryMetricEntry,
  QueryPropertyEntry,
  QuerySegmentEntry,
  QuerySuperEntry,
  RowAddress,
  RowLabelType,
  RowPrefixBehaviors,
  RowTypeComplexOption,
  RowTypeOption,
  SavedCohortItem,
  SelectorType,
  SelectorTypeAggregation,
  SelectorTypeAggregationItem,
  SelectorTypeAggregationLabel,
  SelectorTypeAggregationSection,
  SelectorTypeDefinitions,
  SelectorTypesLabels,
} from './types';
import {stageEntryMove} from './util/moving-rows';
import {EntryRow} from './row-types/base/types';
import {createEntryRow, getNewEmptyEntry} from './row-types/util';
import {UpdateStatus} from './types';

enum DROP_POSITION {
  ABOVE,
  BELOW,
}

/** query-entry-section internal: emitted when an entry is dropped into a group entry */
type DropIntoGroupEvent = CustomEvent<{
  toIndex: RowAddress;
  fromIndex: RowAddress;
}> & {name: `dropIntoGroup`};

interface QueryEntrySectionState {
  activeEntry: number;
  entryPart: EntryPart;
  dataGroups: DataGroup[];
  draggingGripperIndex: number;
  draggingOverStepIndex: number;
  dropPosition: DROP_POSITION;
  placeholders: Map<number, EntryPart>;
  stagedRows: EntryRow[];
  stagedRow: EntryRow;
  featureFlags: Set<ProjectFeatureFlag>;
}

// Defines types of entities that can be added to the list
type TypeList = {[key in SelectorType]?: RowTypeComplexOption};

const LIST_TYPES: TypeList = {
  [ResourceType.Cohort]: {
    label: capitalize(`${SelectorTypesLabels[ResourceType.Cohort]}`),
    value: ResourceType.Cohort,
    icon: `community`,
  },
  [ResourceType.Event]: {
    label: capitalize(`${SelectorTypesLabels[ResourceType.Event]}`),
    value: ResourceType.Event,
    icon: `event`,
  },
  [ResourceType.User]: {
    label: `Profile`,
    value: ResourceType.User,
    icon: `profile`,
  },
};

/**
 * Defines what an entry with a property maps to as a selector type
 * TODO(pablo): figure out direction for entry types and remove this mapping
 */
const PROPERTY_AGGREGATION_TYPE_OVERRIDES = {
  [ResourceType.Event]: SelectorTypeDefinitions.EventPropertyAggregation,
  [ResourceType.User]: SelectorTypeDefinitions.UserPropertyAggregation,
};

type IncludeCustomProperties = `none` | `persisted-only` | `persisted-non-behavioral-only` | `all`;

interface QueryEntrySectionAttrs {
  'add-entry-label': string;
  'add-entry-menu-width': number;
  'allow-cohort-filter': boolean;
  'can-rename-step': boolean;
  'enable-enhanced-list-filters': boolean;
  'group-event-behavior': GroupEventBehaviors;
  'disable-add-grouped-event-reason': string;
  'data-group-id': string;
  'data-groups-enabled': boolean;
  'date-range': string;
  'disable-clone-entry': boolean;
  'disable-delete-entry': boolean;
  'disable-first-time-event-filters': boolean;
  'enable-workspace-filter-restrictions': boolean;
  disabled: boolean;
  'disable-add-entry': boolean;
  'entry-type': EntryType;
  'event-aggregator-functions': MATH_TYPE[];
  'property-aggregator-functions': MATH_TYPE[];
  entries: QuerySuperEntry[];
  'exclude-autotrack-events': boolean;
  'exclude-custom-events': boolean;
  'exclude-dimension-properties': boolean;
  'include-custom-properties': IncludeCustomProperties;
  'is-blocked-people': string;
  'max-data-history-days': number;
  'metrics-mode': boolean;
  'num-exclusion-steps': number;
  'project-has-cohorts': boolean;
  'project-has-people': boolean;
  'project-id': number;
  'user-id': number;
  'row-draggable': boolean;
  'undraggable-rows': number[];
  'row-types': RowTypeOption[];
  'row-index-toggleable': boolean;
  'row-index-toggle-label': string;
  'row-index': number;
  'row-label-prefix': string;
  'row-label-prefix-behavior': RowPrefixBehaviors;
  'row-label-type': RowLabelType;
  'row-limit': number;
  'show-all-events': boolean;
  'show-any-event': boolean;
  'hide-edit-controls': boolean;
  'show-sessions': boolean;
  'show-top-events': boolean;
  'show-special-date-property': boolean;
  'hide-special-date-property-unit': boolean;
  'show-special-dataset-property': boolean;
  'support-b2b-cohorts': boolean;
  'inline-cohorts-override': boolean;
  'time-display-context': DisplayContext;
  'track-name': string;
  'track-properties': UnknownObj;
  'workspace-id': number;
  'project-as-of-date': string;
  'event-names': string[];
  'other-group-by-options': string;
  'hide-events': boolean;
  'can-customize-numeric-buckets': boolean;
}

export interface QueryEntrySectionAttrsJsxProps {
  attrs: Partial<QueryEntrySectionAttrs>;
  props?: {
    isStagingRow: boolean | Partial<QuerySuperEntry>;
  };
}

export class QueryEntrySection extends Component<QueryEntrySectionState, QueryEntrySectionAttrs> {
  private dispatchUpdate: (ev: EntrySectionUpdateEvent) => void;
  private nextEntryKey: number;
  private pendingQueryReason: QueryReason;
  private _featuresStoreCallback;
  private _lookupTablesStoreCallback;

  /**
   * Defines a set of feature flags that are guaranteed to be the same during a given session
   * they are expected to change on a page reload or a re-render if they store value has changed
   */
  static CachedFeatureFlags = [];

  static get attrsSchema(): AttrsSchema<QueryEntrySectionAttrs> {
    return {
      ...super.attrsSchema,
      'add-entry-label': `string`,
      'add-entry-menu-width': {type: `number`, default: 110},
      'allow-cohort-filter': `boolean`,
      'can-rename-step': `boolean`,
      'enable-enhanced-list-filters': `boolean`,
      'group-event-behavior': {
        type: `string`,
        enum: Object.values(GroupEventBehaviors),
        default: GroupEventBehaviors.None,
      },
      'data-group-id': `string`,
      'data-groups-enabled': `boolean`,
      // TODO (pablo): remove this attribute and pass it on the entry data field
      'date-range': `string`,
      'disable-add-entry': {type: `boolean`, description: `when set, hides the Add button from the row controls`},
      'disable-clone-entry': `boolean`,
      'disable-delete-entry': `boolean`,
      'disable-first-time-event-filters': `boolean`,
      'disable-add-grouped-event-reason': `string`,
      'enable-workspace-filter-restrictions': `boolean`,
      disabled: `boolean`,
      'entry-type': {type: `string`, default: EntryType.Event, enum: Object.values(EntryType)},
      'event-aggregator-functions': {
        type: `json`,
        description: `list of event aggregator function choices from primative math types`,
      },
      'property-aggregator-functions': {
        type: `json`,
        description: `list of property aggregator function choices from primative math types`,
      },
      entries: {
        type: `json`,
        default: [],
        description: `interface: EventEntry[]`,
      },
      'exclude-autotrack-events': {
        type: `boolean`,
        description: `whether to hide autotrack events from the events menu`,
      },
      'exclude-custom-events': `boolean`,
      'exclude-dimension-properties': {
        type: `boolean`,
        description: `whether to hide dimension properties from properties menus`,
      },
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
        description: `whether to hide custom properties from properties menus`,
      },
      'is-blocked-people': `boolean`,
      'max-data-history-days': {
        type: `number`,
        description: `max days time menu can query into data history`,
      },
      'metrics-mode': {type: `boolean`, description: `allow property aggregation operations`},
      'num-exclusion-steps': `number`,
      'project-has-cohorts': `boolean`,
      'project-has-people': `boolean`,
      'project-id': `number`,
      'user-id': `number`,
      'row-draggable': {
        type: `boolean`,
        description: `Whether the events can be dragged to rearrange`,
      },
      'undraggable-rows': {
        type: `json`,
        default: [],
        description: `Array of indices for rows that cannot be dragged`,
      },
      'row-types': {
        type: `json`,
        default: [ResourceType.Event],
        description: `Default row types. Interface: RowTypeOption[]`,
      },
      'row-index-toggleable': {type: `boolean`, description: `Whether the row's label is toggleable`},
      'row-index-toggle-label': `string`,
      'row-index': {
        type: `number`,
        default: null,
        description: `if this is a nested entry-section, provide its index in the parent section`,
      },
      'row-label-prefix': {
        type: `string`,
        description: `for a group of rows, indent labels and use this prefix`,
      },
      'row-label-prefix-behavior': {
        type: `string`,
        enum: Object.values(RowPrefixBehaviors),
        description: `how to display and handle interactions with the prefix`,
        default: RowPrefixBehaviors.None,
      },
      'row-label-type': {type: `string`, default: RowLabelType.Numeric, enum: Object.values(RowLabelType)},
      'row-limit': {type: `number`},
      'show-all-events': `boolean`,
      'show-any-event': `boolean`,
      'hide-edit-controls': {
        type: `boolean`,
        description: `show controls that popup modals to edit entities like custom events and properties`,
      },
      'show-sessions': `boolean`,
      'show-top-events': `boolean`,
      'show-special-date-property': {
        type: `boolean`,
        description: `enables special date property and breakdown type for datetime`,
      },
      'hide-special-date-property-unit': {
        type: `boolean`,
        description: `disables the unit selection for special date property break-down`,
      },
      'show-special-dataset-property': `boolean`,
      'support-b2b-cohorts': `boolean`,
      'inline-cohorts-override': `boolean`,
      'time-display-context': {type: `string`, default: DisplayContext.FullLength},
      'track-name': `string`,
      'track-properties': {
        type: `json`,
        default: {},
        description: `If specified, adds extra properties to the track event. These will override ones from query-builder`,
      },
      'workspace-id': `number`,
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'event-names': {type: `json`, default: null},
      'other-group-by-options': {type: `string`, default: `[]`},
      'hide-events': `boolean`,
      'can-customize-numeric-buckets': {type: `boolean`},
    };
  }

  constructor() {
    super();
    this.nextEntryKey = 1;
    this.dispatchUpdate = debounce(
      (ev) => {
        ev.detail.queryReason = this.pendingQueryReason;
        delete this.pendingQueryReason;
        this._dispatchUpdate(ev);
      },
      100,
      {leading: true},
    );
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      defaultState: {
        activeEntry: null,
        entryPart: null,
        dataGroups: [DEFAULT_DATA_GROUP],
        draggingGripperIndex: null,
        draggingOverStepIndex: null,
        dropPosition: null,
        placeholders: new Map(),
        stagedRows: [],
        stagedRow: null,
        featureFlags: new Set<ProjectFeatureFlag>(),
      },

      helpers: {
        Action,
        EntryPart,
        camelCase,
        ControlBarAction,
        ControlBarActionItems,
        GroupEventBehaviors,
        RowPrefixBehaviors,
        EntryType,
        ResourceType,
        RowLabelType,
        serializeAttributes,
        SelectorTypeDefinitions,
        isGroupedEntry,

        canDeleteEntry: () => this.attr(`disable-delete-entry`) === false,
        getSelectedTypeLabel: (entryType) => SelectorTypesLabels[entryType],
        isExclusionStepsSection: () => this.attr(`entry-type`) === EntryType.ExclusionEvent,
        isDroppingAbove: (index) =>
          this.state.draggingOverStepIndex === index && this.state.dropPosition === DROP_POSITION.ABOVE,
        isDroppingBelow: (index) =>
          this.state.draggingOverStepIndex === index && this.state.dropPosition === DROP_POSITION.BELOW,
        preventTracking: (ev: Event): void => preventTracking(ev),
        propagateEvent: (ev) => propagateEvent(this, ev),

        useNewMetricsMode: () => useNewMetricsMode(this.attr(`project-id`)).useNewMetricsMode,
        selectorTheme: () => useNewMetricsMode(this.attr(`project-id`)).selectorTheme,
        selectorType: () => useNewMetricsMode(this.attr(`project-id`)).selectorType,

        getAddListTypeForType: (type: SelectorType) => this.getAddListTypeItems()[type],
        getAddListTypeItems: () => Object.values(this.getAddListTypeItems({expandedView: false})),
        getQueryBlockConfig: (entry: QuerySuperEntry) => this.getQueryBlockConfig(entry.type),

        canPerformPropertyAggregation: (entry) =>
          this.attr(`metrics-mode`) && canPerformPropertyAggregation(entry.type),

        /** Callback for changes from child query-entry-section */
        onGroupedSectionUpdate: (index: number, ev: CustomEvent<{entries: QueryGroupedEntry['event']}>) => {
          const {entries} = ev.detail;
          const currentEntry = this.getEntries()[index];
          if (
            entries.length === 1 &&
            currentEntry.type === SelectorTypeDefinitions.GroupedEvent &&
            currentEntry.event.length > 1
          ) {
            this.updateEntries(Action.Ungroup, {
              entry: entries[0],
              index,
            });
          } else {
            this.updateEntries(Action.Edit, {
              entry: {
                ...currentEntry,
                type: SelectorTypeDefinitions.GroupedEvent,
                event: entries,
                filters: [],
              },
              index,
            });
          }
        },

        getNestedSectionAttrs: (): Partial<QueryEntrySectionAttrs> =>
          pick(this.attrs(), [
            `allow-cohort-filter`,
            `data-groups-enabled`,
            `data-group-id`,
            `can-customize-numeric-buckets`,
            `can-rename-step`,
            `enable-enhanced-list-filters`,
            `disable-first-time-event-filters`,
            `enable-workspace-filter-restrictions`,
            `exclude-autotrack-events`,
            `exclude-custom-events`,
            `exclude-dimension-properties`,
            `include-custom-properties`,
            `is-blocked-people`,
            `max-data-history-days`,
            `show-all-events`,
            `show-any-event`,
            `hide-edit-controls`,
            `show-sessions`,
            `show-top-events`,
            `show-special-date-property`,
            `hide-special-date-property-unit`,
            `show-special-dataset-property`,
            `support-b2b-cohorts`,
            `workspace-id`,
            `user-id`,
            `project-id`,
            `project-as-of-date`,
            `project-has-people`,
            `project-has-cohorts`,
            `track-name`,
            `hide-events`,
          ]),

        isFirstTimeEventFilter: (filter: PropertyFilter) => isFirstTimeEventFilter(filter),
        getEntryFilters: (index: number) => {
          const [behavioralFilters, filters] = partition(this.getEntry(index).filters, isFirstTimeEventFilter);

          return {
            [EntryPart.Filters]: filters,
            [EntryPart.BehavioralFilters]: behavioralFilters,
          };
        },

        /** checks if an entry contains a first time event filter, in any position */
        hasFirstTimeEventFilter: (index: number) => this.getEntry(index).filters.some(isFirstTimeEventFilter),

        onFilterSelected: ({detail: {items, queryReason}}: CustomEvent, index: number, source: EntryPart): void => {
          // behavioral filters and regular filters are currently appended to the filters list
          const filters = {
            ...this.helpers.getEntryFilters(index),
            [source]: items,
          };

          const entry: QuerySuperEntry = {
            ...this.getEntry(index),
            filters: [...filters[EntryPart.Filters], ...filters[EntryPart.BehavioralFilters]],
          };
          this.updateEntries(Action.Edit, {entry, index, queryReason});
        },

        onFiltersOperatorSelected: (ev: CustomEvent, index): void => {
          const entry: QuerySuperEntry = {
            ...this.getEntry(index),
            filtersOperator: ev.detail.value,
          };
          this.updateEntries(Action.Edit, {entry, index});
        },

        onControlSelect: (value, index) => {
          switch (value) {
            case ControlBarAction.Filter:
              return this.helpers.showPlaceholderForEntry(EntryPart.Filters, index);
            case ControlBarAction.FirstTimeFilter:
              return this.applyFirstTimeEventFilter(index);
            case ControlBarAction.Rename:
              return this.promptRename(index);
            case ControlBarAction.Duplicate:
              return this.helpers.onEventActionClick(Action.Clone, index);
            case ControlBarAction.Delete:
              return this.helpers.onEventActionClick(Action.Remove, index);
            case ControlBarAction.CustomizeBuckets:
              return this.onCustomizeBuckets(index);
            case ControlBarAction.AddGroupedEvent:
              return this.addOrConvertToGroupedEvent(index);
            case ControlBarAction.MapLookupTable:
              return this.onMapLookupTable(index);
            case ControlBarAction.UnmapLookupTable:
              return this.onUnmapLookupTable(index);
          }
        },

        getEntryOverflowControls: (index: number) => {
          const row = this.getRows()[index];
          const {entry} = row;
          const isCohortEntry = entry.type === ResourceType.Cohort;
          const isPropertyEntry = isPropertySelectorType(entry.type);
          const isPlaceholder = row.isPlaceholder();
          const eventValue = (entry as QueryEventEntry).event.value;
          const isTopEvents = eventValue === SPECIAL_EVENTS.TOP_EVENTS;
          const canApplyFilter = !isPlaceholder && !isCohortEntry && !isPropertyEntry;
          const canApplyFirstTimeEventFilter =
            canApplyFilter &&
            !this.helpers.hasFirstTimeEventFilter(index) &&
            !this.attr(`disable-first-time-event-filters`) &&
            (entry.type === ResourceType.Event ||
              entry.type === SelectorTypeDefinitions.EventPropertyAggregation ||
              entry.type === SelectorTypeDefinitions.FlowsEvent);
          const isFirstTimeEventFilterDisabled =
            canApplyFirstTimeEventFilter &&
            (isTopEvents || [SPECIAL_EVENTS.SESSION_START, SPECIAL_EVENTS.SESSION_END].includes(eventValue));

          const canRenameStep =
            !isPlaceholder &&
            !isInlineCohortItem(entry.event as SavedCohortItem) &&
            this.attr(`can-rename-step`) &&
            !isTopEvents;
          const canDeleteEntry = !this.helpers.useNewMetricsMode() && this.helpers.canDeleteEntry();
          const canDuplicate = !isPlaceholder && !this.attr(`disable-clone-entry`) && this.helpers.canAddRow();
          const canCustomizeBuckets = this.canCustomizeBuckets(index);

          let canAddGroupedEvent =
            entry.type === ResourceType.Event && this.attr(`group-event-behavior`) !== GroupEventBehaviors.None;

          if (canAddGroupedEvent && this.attr(`group-event-behavior`) === GroupEventBehaviors.Append) {
            const lastRow = last(this.getRows());
            canAddGroupedEvent = !lastRow.isPlaceholder();
          }

          const isAddGroupedEventDisabled = canAddGroupedEvent && this.attr(`disable-add-grouped-event-reason`);
          const disabledAddGroupedEventReason = isAddGroupedEventDisabled
            ? this.attr(`disable-add-grouped-event-reason`)
            : null;

          return [
            {
              items: [
                canApplyFilter && ControlBarActionItems[ControlBarAction.Filter],
                canApplyFirstTimeEventFilter && {
                  ...ControlBarActionItems[ControlBarAction.FirstTimeFilter],
                  ...(isFirstTimeEventFilterDisabled
                    ? {
                        tooltip: {
                          text: `To apply 1st time filter, select a different event`,
                        },
                        isDisabled: true,
                      }
                    : {}),
                },
                canRenameStep && ControlBarActionItems[ControlBarAction.Rename],
                canDuplicate && ControlBarActionItems[ControlBarAction.Duplicate],
                canDeleteEntry && ControlBarActionItems[ControlBarAction.Delete],
                canCustomizeBuckets && ControlBarActionItems[ControlBarAction.CustomizeBuckets],
                canAddGroupedEvent && {
                  ...ControlBarActionItems[ControlBarAction.AddGroupedEvent],
                  ...(isAddGroupedEventDisabled
                    ? {
                        tooltip: {
                          text: disabledAddGroupedEventReason,
                        },
                        isDisabled: true,
                      }
                    : {}),
                },
                this.canMapLookupTable(row) && ControlBarActionItems[ControlBarAction.MapLookupTable],
                this.canUnmapLookupTable(row) && ControlBarActionItems[ControlBarAction.UnmapLookupTable],
              ].filter(Boolean),
            },
          ];
        },

        /** Disable the adding button when row-limit is 1, the placeholder row is added */
        canAddRow: () => {
          if (this.attr(`group-event-behavior`) === GroupEventBehaviors.Append) {
            return false;
          }

          const rowLimit = this.attr(`row-limit`);

          if (rowLimit === 1) {
            return false;
          }

          return rowLimit > 0 ? this.helpers.getEntries().length < rowLimit : true;
        },

        /** Aborts staging new row when delete action is performed */
        onEventActionClick: (action: Action, index: number) => {
          if (action === Action.Remove && this.isStagedEntry(index)) {
            this.abortRowStage();
          } else {
            this.updateEntries(action, {index});
          }
        },

        /** TODO this only deletes custom events in the same query entry section, and doesn't
         * bubble up into any others that might be necessary such as for impact, flows, or funnels
         */
        handleDeletedCustomEvent: (ev) => {
          // get all indices of the current entries and remove each of the entries in which
          // the id is this
          let indexCount = 0;
          this.getEntries().forEach((entry: QueryEventEntry, index) => {
            if (entry.event.id === ev.detail.id) {
              // calling removal directly decreases the size of the entries and can delete the wrong one
              // so we need to update the indexCount to get the proper index
              this.helpers.onEventActionClick(Action.Remove, index - indexCount);
              indexCount += 1;
            }
          });
        },

        isStagedRow: (index: number) => this.isStagedEntry(index),
        isNestedSection: () => this.attr(`group-event-behavior`) === GroupEventBehaviors.Append,

        /** Disables adding until last row is commited */
        isAddingDisabled: () => {
          return this.getRows(true).some((row) => row.isPlaceholder()) || this.attr(`disabled`);
        },

        getRowLabelPrefix: (index) => {
          switch (this.attr(`row-label-prefix-behavior`)) {
            case RowPrefixBehaviors.None:
              return undefined;
            case RowPrefixBehaviors.FirstOnly:
              return index === 0 ? this.attr(`row-label-prefix`) : null;
            case RowPrefixBehaviors.EveryRow:
              return this.attr(`row-label-prefix`);
          }
        },
        getRowLabel: (index: number): string => {
          const entry = this.getEntry(index);

          // only toggle if label is provided and event is set as toggled.
          if (this.attr(`row-index-toggle-label`) && entry && entry.entryIndexLabelToggle) {
            return this.attr(`row-index-toggle-label`);
          }

          switch (this.attr(`row-label-type`)) {
            case RowLabelType.Alpha:
              return getAlphaLabelFromIndex(index);
            case RowLabelType.Numeric:
              return `${index + 1}`;
            default:
              return `-`;
          }
        },

        showPlaceholderForEntry: (type: EntryPart, index: number) => {
          const {placeholders} = this.state;
          placeholders.set(index, type);

          this.update({placeholders});
        },

        onEntryAggregationFunctionSelected: ({
          index,
          type,
          isSubAggregation = false,
        }: {
          index: number;
          type: SelectorTypeAggregation;
          isSubAggregation: boolean;
        }) => this.onEntryAggregationFunctionSelected(index, type, isSubAggregation),

        onEntryPropertyRemoved: (index: number) => this.onEntryPropertyRemoved(index),

        onEntryPropertySelected: (index: number, property: PropertyItem) =>
          this.onEntryPropertySelected(index, property),

        getEntry: (index): QuerySuperEntry => this.getEntries({appendPlaceholder: true})[index],
        getEntries: (): QuerySuperEntry[] => this.getEntries({appendPlaceholder: true}),

        setActiveEntry: (state, index: number, part: EntryPart): void => {
          const {activeEntry, entryPart} = this.state;

          if (state === `close`) {
            this.update({placeholders: new Map()});
          }

          if (state === `open`) {
            this.setActiveEntry(index, part);
          } else if (activeEntry === index && entryPart === part) {
            if (this.isStagedEntry(index) && !this.helpers.useNewMetricsMode()) {
              this.abortRowStage();
            } else {
              this.setActiveEntry(null, null);
            }
          }
        },

        onSelectMenuChange: (ev, index: number, part: EntryPart) => {
          const {isOpen} = ev.detail;
          const status = isOpen ? `open` : `close`;

          this.helpers.setActiveEntry(status, index, part);
        },

        /** Changes type of staged entry */
        onAddTypeSelected: (type: SelectorType | SelectorTypeAggregation) => this.addEntryRow(type),

        isDragEnabled: (index: number): boolean => {
          return (
            this.attr(`row-draggable`) &&
            !this.helpers.isAddingDisabled() &&
            !this.attr(`undraggable-rows`).includes(index) &&
            !this.attr(`disabled`)
          );
        },

        /**
         * Starts a drag operation only when it initiates from a gripper control,
         * this is achieved by setting the event index when a gripper is pressed
         */
        onEntryStepDragStart: (ev: DragEvent, fromIndex: number): void => {
          if (this.state.draggingGripperIndex !== fromIndex) {
            return ev.preventDefault();
          }

          this.writeRowAddress(ev, fromIndex);
          ev.dataTransfer.effectAllowed = `move`;
        },

        onEntryStepDragEnter: (index: number): void => {
          this.update({draggingOverStepIndex: index});
        },

        onEntryStepDragOver: (ev: DragEvent, index: number): void => {
          ev.preventDefault();
          ev.dataTransfer.dropEffect = `move`;
          this.update({draggingOverStepIndex: index, dropPosition: this.getDropPosition(ev)});
        },

        onEntryStepDragClear: (): void => this.update({draggingOverStepIndex: null, dropPosition: null}),

        onEntryStepDropIntoGroup: (ev: DropIntoGroupEvent) => {
          this.helpers.onEntryStepDragClear();
          this.handleEntryStepDrop(ev.detail.fromIndex, ev.detail.toIndex);
        },
        onEntryStepDrop: (ev: DragEvent, dropIndex: number): void => {
          ev.preventDefault();
          ev.stopPropagation();
          this.helpers.onEntryStepDragClear();

          const fromAddress = this.readRowAddress(ev);
          const isDroppingIntoGroupedEntry = this.attr(`group-event-behavior`) === GroupEventBehaviors.Append;
          if (isDroppingIntoGroupedEntry) {
            const toAddress = this.getRowAddress(dropIndex);
            if (this.getDropPosition(ev) === DROP_POSITION.BELOW) {
              toAddress[1]++;
            }

            // emit it
            this.dispatchEvent(
              new CustomEvent(`dropIntoGroup`, {
                detail: {
                  toIndex: toAddress,
                  fromIndex: fromAddress,
                },
              }) as DropIntoGroupEvent,
            );
          } else {
            let toIndex = dropIndex;
            // Insert after the drop target index if dropping below
            if (this.getDropPosition(ev) === DROP_POSITION.BELOW) {
              toIndex++;
            }
            // Adjust the index to account for removing the item itself if we are moving it down the list
            if (fromAddress.length === 1 && toIndex > fromAddress[0]) {
              toIndex--;
            }

            this.handleEntryStepDrop(fromAddress, [toIndex]);
          }
        },

        onEventSelected: (ev: CustomEvent, index: number): void => {
          const {item, trackingProps, queryReason} = ev.detail;
          this.onEntryEventSelected(index, item, {trackingProps, queryReason});
        },

        onGroupEntryIndexLabelClicked: () => {
          this.dispatchEvent(new CustomEvent(`entryIndexLabelClicked`));
        },

        onEntryIndexLabelClicked: (index: number) => {
          const entryAtIndex = this.attr(`row-index-toggleable`) && this.getEntry(index);
          if (entryAtIndex) {
            const entry: QuerySuperEntry = {
              ...entryAtIndex,
              entryIndexLabelToggle: !entryAtIndex.entryIndexLabelToggle,
            };
            this.updateEntries(Action.Edit, {entry, index});
            this.dispatchTrack(TrackingEvent.EventIndexLabelClicked, {
              ...this.getEntryTrackingProps(index),
              'Previous Label': this.helpers.getRowLabel(index),
            });
          }
        },

        onGripperMouseDown: (index: number): void => {
          this.update({draggingGripperIndex: index});
        },

        onGripperMouseLeave: (): void => {
          this.update({draggingGripperIndex: null});
        },

        onDateRangeChange: (ev: CustomEvent, index: number) => {
          const entryAtIndex = this.getEntry(index);
          if (this.helpers.isDateRangeType(entryAtIndex.type)) {
            const entry = {
              ...entryAtIndex,
              dateRange: (ev.detail as DateRange).getAttributes(),
            };
            this.updateEntries(Action.Edit, {entry, index});
            this.dispatchTrack(this.getTrackingEvent(entryAtIndex.type, Action.SelectDateRange), {
              ...ev.detail.props,
              'Entry index': index,
            });
          }
        },

        getDefaultDateValue: () =>
          this.attr(`date-range`) ||
          DateRangeFactory.createDateRange({}, {displayContext: this.attr(`time-display-context`)}).getAttributes(),

        isUserBasedType: (type: SelectorType) => isUserBasedType(type),
        isEventBasedType: (type: SelectorType) => isEventBasedType(type),

        isDateRangeType: (type: SelectorType) =>
          type === SelectorTypeDefinitions.Behavior ||
          type === SelectorTypeDefinitions.EventLaunch ||
          this.helpers.getAddListTypeForType(type)?.includeDateRange,

        // TODO(mack): There's tracking split among this function and in updateEntries. Merge the tracking
        // logic to avoid confusion.
        trackEntrySelectorEvent: (ev: CustomEvent, index: number) => {
          preventTracking(ev);

          // This is a hack due to (what appears to be) a race condition surfaced in our WCT tests.
          // I spent a few hours trying to identify root cause but the system is sufficiently complex
          // enough and I'm sufficiently ignorant enough that I couldn't figure it out. The bug manifests itself
          // as this.helpers.getEntries() is an empty list.
          if (this.helpers.getEntries().length <= index) {
            return;
          }

          const type = this.helpers.getEntries()[index].type;
          const commonProps = {
            'Entry index': index,
            'Entry type': type,
          };

          const {eventName, eventPrefix, props} = ev.detail;
          if (eventName === `${eventPrefix}${QueryBlockTrackingEvent.HOVER_CONTEXT_PANEL}`) {
            ev.stopPropagation();
            const eventName = ev.detail.eventName.replace(eventPrefix, ``);
            this.dispatchTrack(eventName, {
              ...props,
              ...commonProps,
            });
            return;
          }

          if (!ev.cancelable || isPropertySelectorType(type)) {
            // Filter and segment actions are tracked elsewhere
            return;
          }

          let trackingAction = null;

          switch (eventName) {
            case `Add property filter`:
              trackingAction = Action.AddFilter;
              break;
            case `Edit property filter`:
              trackingAction = Action.EditFilter;
              break;
            case `Remove property filter`:
              trackingAction = Action.RemoveFilter;
              break;
            case `Select date range`:
              trackingAction = Action.SelectDateRange;
              break;
            case `Select Event`:
            case `Select Property`:
            case `Select segment`:
              trackingAction = Action.Select;
              break;
            default:
              throw new Error(`tracking not defined for entry selector event: ${eventName}`);
          }

          this.dispatchTrack(this.getTrackingEvent(type, trackingAction), {
            ...props,
            ...commonProps,
          });
        },

        aggregationMenuContextPanelTransform: (item: SelectorTypeAggregationItem) => {
          if (!item) {
            return null;
          }

          return {
            header: this.formatDataGroupLabel(item.label),
            description: this.formatDataGroupLabel(item.value.description),
          };
        },

        overflowControlsContextPanelTransform: (item: SectionItem) => ControlBarActionItems[item?.value]?.tooltipData,

        /** Returns list of math function available for aggregations */
        getSelectorAggregationsForEntry: (index: number) => this.getSelectorAggregationsForEntry(this.getEntry(index)),

        /** Returns list of sub aggregations for a given entry */
        getSelectorSubAggregationsForEntry: (index: number) =>
          this.getSelectorSubAggregationsForEntry(this.getEntry(index)),

        /**
         * Returns selected math function for a given entry
         *
         * TODO(pablo): selected top level aggregator is derived from the 2-level aggregation list, this
         * needs to change to explicitly define both selector types to accommodate per-user property aggregation
         */
        getSelectorAggregationLabelForEntry: (index: number) => {
          const aggregation = this.getSelectedAggregationForEntry(this.getEntry(index));

          // if on the old metrics mode, always surface the 2-level aggregation
          const subAggregation = this.helpers.getSelectorSubAggregationLabelForEntry(index);
          if (!this.helpers.useNewMetricsMode() && subAggregation) {
            return subAggregation;
          }

          return this.getAggregationLabel(aggregation);
        },

        /**
         * Returns selected 2-level math function for a given entry
         */
        getSelectorSubAggregationLabelForEntry: (index: number) => {
          const selectedAggregation = this.getSelectedSubAggregationForEntry(this.getEntry(index));

          return selectedAggregation && this.getAggregationLabel(selectedAggregation);
        },

        /**
         * TODO(pablo): this needs to go away.
         * Exclusion steps related fns.
         */
        exclusionSteps: {
          getExclusionStep: (index: number) => {
            const entry = this.getEntry(index);
            return {isEntireRange: entry.excludeAll, range: entry.excludeRange};
          },

          getNumExclusionSteps: (): number => {
            return this.attr(`num-exclusion-steps`);
          },

          onExclusionStepSelected: (ev: CustomEvent, index: number) => {
            const {isEntireRange: excludeAll, range: excludeRange}: RangeSelection = ev.detail;

            const entryUpdate: QuerySuperEntry = {
              ...this.getEntry(index),
              excludeAll,
              excludeRange,
            };
            this.updateEntries(Action.Edit, {entry: entryUpdate, index});

            this.dispatchTrack(TrackingEvent.EditExclusionStepRange, {
              'Exclude all': excludeAll,
              'Exclude from': excludeRange.from || null,
              'Exclude to': excludeRange.to || null,
            });
          },
        },

        getCustomBucketPaneAttributes: () => {
          const bucket = this.getCustomBucketByIndex(this.state.activeEntry);
          const selectedBucket = bucket || DefaultCustomBucket;
          return {
            'max-num-buckets': 1000,
            'reset-bucket': !!bucket,
            'selected-bucket': JSON.stringify(selectedBucket),
            'selected-unit': `units`,
            header: `Customize Range`,
          };
        },

        onCustomizeBucketsMenuChange: (ev) => {
          if (ev.detail.state === Visibility.Closed) {
            this.abortRowStage();
          }
        },

        onChangeSelectedCustomBucket: (ev) => {
          const {activeEntry} = this.state;
          const prevCustomBucket = this.getCustomBucketByIndex(activeEntry);
          this.updateCustomBucket(activeEntry, ev.detail);
          const customBucket = this.getCustomBucketByIndex(activeEntry);
          this.dispatchTrack(TrackingEvent.ApplyCustomBucket, {
            ...this.getEntryTrackingProps(activeEntry),
            ...this.getCustomBucketTrackingProps(customBucket),
            'Is editing': !!prevCustomBucket,
          });
          this.abortRowStage();
        },

        onResetCustomBucket: () => {
          const {activeEntry} = this.state;
          this.updateCustomBucket(activeEntry, null);
          this.dispatchTrack(TrackingEvent.ResetCustomBucket, {
            ...this.getEntryTrackingProps(activeEntry),
          });
          this.abortRowStage();
        },

        /** helpers are this bound, a little helper function that simply re-draws the UI when any store values change */
        updateAfterStoreValueChange: () => this.update(),
      },
    };
  }

  /**
   * Sets the component to a row staging mode and optionally sets additional entry fields
   */
  set isStagingRow(partialStagedRow: boolean | Partial<QuerySuperEntry>) {
    const {stagedRow} = this.state;

    if (partialStagedRow && !stagedRow) {
      const index = this.state.stagedRows.length;
      const type = Object.values(this.getAddListTypeItems())[0].value;
      const entry = {
        ...this.getNewEntry(stagedRow?.entry.type ?? type, {...stagedRow?.entry}),
        ...(typeof partialStagedRow === `boolean` ? {} : (partialStagedRow as QuerySuperEntry)),
      };

      // avoid auto opening the first staged row
      this.update({stagedRow: this.createEntryRow(index, entry)});
      if (!(this.attr(`row-limit`) === 1 && this.state.stagedRows.length === 0)) {
        this.setActiveEntry(index, EntryPart.Value);
      }
    }
  }

  /** Construct a row address for this index */
  getRowAddress(index: number): RowAddress {
    const rowAddr = [index];
    if (Number.isInteger(this.attr(`row-index`))) {
      rowAddr.unshift(this.attr(`row-index`));
    }

    return rowAddr as RowAddress;
  }

  /** Write index as the row address into ev.dataTransfer */
  writeRowAddress(ev: DragEvent, index: number): void {
    ev.dataTransfer.setData(`application/json`, JSON.stringify(this.getRowAddress(index)));
  }

  /** Read a row address off of a DragEvent dataTransfer */
  readRowAddress(ev: DragEvent): RowAddress {
    return JSON.parse(ev.dataTransfer.getData(`application/json`)) as RowAddress;
  }

  public async connectedCallback() {
    // blocking connectedCallback to fetch a resource is almost always a bad idea,
    // we make an exception for this component to locally obtain a copy of cached
    // project feature flags to prevent flashes of different UIs throughout a single
    // session, whenever a new report is loaded, the refreshed feature flags will be
    // used to render the UI
    if (QueryEntrySection.CachedFeatureFlags.length) {
      const {stagedRow} = this.state;
      await this.getFeatureFlags();
      this._featuresStoreCallback = null;
      if (stagedRow) {
        this.update({stagedRow: this.createEntryRow(stagedRow.index, stagedRow.entry)});
      }
    }

    this.updateStagedRowsFromEntries();
    super.connectedCallback();

    this.fetchDataGroups();
    this._lookupTablesStoreCallback = ({fromUpdate}) => {
      if (fromUpdate) {
        // only refresh on manual update (a temp mapping is registered / unregistered)
        this.update();
      }
    };
    lookupTablesStore.subscribe(this._lookupTablesStoreCallback);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._featuresStoreCallback && featuresStore.unsubscribe(this._featuresStoreCallback);
    this._featuresStoreCallback = null;
    lookupTablesStore.unsubscribe(this._lookupTablesStoreCallback);
    this._lookupTablesStoreCallback = null;
  }

  public attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);

    if (attr === `row-types`) {
      this.abortRowStage();
    } else if (attr === `entries`) {
      this.updateStagedRowsFromEntries();
    } else if ([`project-id`, `workspace-id`].includes(attr) && this.initialized) {
      this.fetchDataGroups();
    }
  }

  getFeatureFlags() {
    return new Promise((resolve) => {
      this._featuresStoreCallback = () => {
        const featureFlags = new Set<ProjectFeatureFlag>();
        for (const flag of QueryEntrySection.CachedFeatureFlags) {
          featuresStore.value?.hasProjectFeatureFlag(flag) && featureFlags.add(flag);
        }

        this.update({featureFlags});

        featuresStore.unsubscribe(this._featuresStoreCallback);
        resolve(true);
      };
      featuresStore.subscribe(this._featuresStoreCallback);
      featuresStore.fetch({projectId: this.attr(`project-id`)});
    });
  }

  updateStagedRowsFromEntries() {
    this.update({
      placeholders: new Map(),
      stagedRows: this.attr(`entries`).map((entry, index) => {
        const entryKey =
          index < this.state.stagedRows.length ? this.state.stagedRows[index].entry.entryKey : this.nextEntryKey++;
        const type = entry.type || ResourceType.Event;
        const mappedType =
          entry.property && PROPERTY_AGGREGATION_TYPE_OVERRIDES[type]
            ? PROPERTY_AGGREGATION_TYPE_OVERRIDES[type]
            : type;

        return this.createEntryRow(index, {
          ...entry,
          entryKey,
          // TODO: User entry support is limited to one selection, this could
          // change so that an option per data group is available
          ...(isUserBasedType(type) ? {readOnlyEvent: true} : {}),

          // TODO(pablo): remove this temporal type override once internal testing is complete
          type: mappedType,
        });
      }),
    });

    // handle single row limits by always displaying a staged row
    if (this.attr(`row-limit`) === 1) {
      const {stagedRows, stagedRow} = this.state;
      if (stagedRows.length === 0 && !stagedRow) {
        this.isStagingRow = true;
      } else if (stagedRows.length > 0 && stagedRow) {
        // abort currently staged row
        this.abortRowStage();
      }
    }
  }

  public getDropPosition(ev: DragEvent) {
    const dropTarget = ev.currentTarget as HTMLElement;
    const rect = dropTarget.getBoundingClientRect();
    return ev.clientY - (rect.top + rect.height / 2) < 0 ? DROP_POSITION.ABOVE : DROP_POSITION.BELOW;
  }

  /**
   * Shifts the step at fromIndex to toIndex,
   * then triggers an event update to requery the data
   */
  public handleEntryStepDrop(index: RowAddress, toIndex: RowAddress): void {
    if (isRowAddressEqual(index, toIndex)) {
      return;
    }
    const trackingProps = {
      'From index': index.join(`.`),
      'To index': toIndex.join(`.`),
    };

    let entries = this.getEntries();
    let entry: QuerySuperEntry = null;

    ({entries, entry} = stageEntryMove(index, toIndex, entries));

    entry.persistedEntry = null;

    this.commitAndTrackUpdate(entries, entry, {trackingProps});

    function isRowAddressEqual(a: RowAddress, b: RowAddress) {
      return a[0] === b[0] && a[1] === b[1];
    }
  }

  /**
   * Generates a new entry with a default operator applied
   */
  public getNewEntry(
    type: SelectorType,
    {
      aggregationOperator,
      event,
      dateRange,
    }: {
      event?: EntryItem;
      aggregationOperator?: MATH_TYPE;
      dateRange?: Partial<DateRange>;
    } = {},
  ): QuerySuperEntry {
    const placeHolderEntry = this.getPlaceHolderEventEntry(type);
    const isDateRangeType = this.helpers.isDateRangeType(type);

    // TODO(pablo): move all of these QuerySuperEntry handlers
    // to different classes to avoid generics in this class
    const emptyEntry = getNewEmptyEntry(type);
    if (emptyEntry) {
      return emptyEntry;
    }

    return {
      ...placeHolderEntry,
      ...(this.attr(`data-group-id`) ? {dataGroupId: this.attr(`data-group-id`)} : null),
      ...(isDateRangeType ? {dateRange: dateRange ?? this.helpers.getDefaultDateValue()} : {}),
      aggregationOperator: aggregationOperator ?? this.getEntryDefaultOperator(type),
      aggregationOperatorPerUser: null,
      event: event || placeHolderEntry.event,

      // TODO: User entry support is limited to one selection, this could
      // change so that an option per data group is available
      ...(isUserBasedType(type) ? {readOnlyEvent: true} : {}),
    } as QuerySuperEntry;
  }

  public getEntryDefaultOperator(type: SelectorType) {
    // TODO(mack): Return null for filter/segment selector types
    return type === ResourceType.Cohort ? PRIMATIVE_MATH_TYPES.UNIQUE : PRIMATIVE_MATH_TYPES.TOTAL;
  }

  /**
   * Returns an aggregator function for a given selector type aggregation
   */
  public getSelectorTypeDefaultAggregator(type: SelectorTypeAggregation): MATH_TYPE {
    return type.subAggregations ? type.subAggregations[0].value.operator : type.operator;
  }

  /**
   * Returns an empty entry for a given source
   */
  public getPlaceHolderEventEntry(type: SelectorType): QueryEventEntry | QueryPropertyEntry {
    const forExclusionStep = this.attr(`entry-type`) === EntryType.ExclusionEvent;

    const event = null;
    if (!isPropertySelectorType(type)) {
      const label = this.attr(`add-entry-label`) || `Select ${SelectorTypesLabels[type]}`;
      return {
        ...(forExclusionStep ? EMPTY_EXCLUSION_EVENT_ENTRY : EMPTY_EVENT_ENTRY),
        event: {
          ...EMPTY_EVENT_ENTRY.event,
          label,
        },
        type,
      } as QueryEventEntry;
    }

    return {
      // TODO(mack): Don't set EMPTY_EVENT_ENTRY fields for segment/property selector types
      ...(forExclusionStep ? EMPTY_EXCLUSION_EVENT_ENTRY : EMPTY_EVENT_ENTRY),
      type,
      event,
    };
  }

  public getSelectedPropertyForEntry(entry: QuerySuperEntry) {
    return entry && entry.property;
  }

  /**
   * Returns the top level aggregation for a given entry
   *
   * TODO(pablo): selected top level aggregator is derived from the 2-level aggregation list, this
   * needs to change to explicitly define both selector types to accommodate per-user property aggregation
   */
  public getSelectedAggregationForEntry(entry: QueryMetricEntry) {
    return FlattenSelectorTypeAggregations.find(
      ({value}) =>
        value.selectorType === entry.type &&
        (entry.aggregationOperatorPerUser
          ? value.perUserOperator === entry.aggregationOperatorPerUser
          : !value.perUserOperator && value.operator === (entry.aggregationOperator || PRIMATIVE_MATH_TYPES.TOTAL)),
    );
  }

  public getAggregationLabel(aggregation: SelectorTypeAggregationItem) {
    const useNewMetricsMode = this.helpers.useNewMetricsMode();
    const label = (useNewMetricsMode ? aggregation.value.shortLabel : aggregation.value.shortName) ?? aggregation.label;

    return this.formatDataGroupLabel(label);
  }

  /**
   * Returns the sub level aggregation for a given entry
   */
  public getSelectedSubAggregationForEntry(entry: QueryMetricEntry) {
    const subAggregations = this.getSelectorSubAggregationsForEntry(entry);

    return subAggregations.find(
      ({value}) => value.operator === entry.aggregationOperator && value.selectorType === entry.type,
    );
  }

  /**
   * Filters aggregation functions to match the provided selector types and optionally defined operators
   */
  public filterSelectorTypeAggregationSections(
    filters: {[key in SelectorType]?: MATH_TYPE[]},
    sourceSections: SelectorTypeAggregationSection[],
    isBehaviorSelectorType: boolean,
  ): Array<Section<SelectorTypeAggregation>> {
    const sections: Array<Section<SelectorTypeAggregation>> = sourceSections.map((section) => ({
      ...section,
      label: this.formatDataGroupLabel(section.label),
      items: this.filterSelectorTypeAggregation(section.items, filters, isBehaviorSelectorType)
        .map((item) => ({
          ...item,
          label: this.formatDataGroupLabel(item.label),
          subSections: item.subSections
            ? this.filterSelectorTypeAggregationSections(filters, item.subSections, isBehaviorSelectorType)
            : null,
        }))
        .filter((item) => (item.subSections ? itemHasSubSections(item) : true)),
    }));

    return sections.filter(isPopulatedSection);
  }

  public filterSelectorTypeAggregation(
    aggregations: SelectorTypeAggregationItem[],
    filters: {[key in SelectorType]?: MATH_TYPE[]},
    isBehaviorSelectorType: boolean,
  ): Array<SectionItem<SelectorTypeAggregation>> {
    return aggregations
      .filter((item) => {
        const type = item.value?.selectorType;
        const matchesType = type ? Object.prototype.hasOwnProperty.call(filters, type) : true;
        let matchesOperator = filters[type] ? filters[type].includes(item.value?.operator) : true;

        matchesOperator =
          item.value?.operator === PRIMATIVE_MATH_TYPES.MOST_FREQUENT && !isBehaviorSelectorType
            ? false
            : matchesOperator;

        return matchesType && matchesOperator;
      })
      .map((item) => ({
        ...item,
        label: this.formatDataGroupLabel(item.label),
      }));
  }

  /**
   * Returns aggregation functions for all supported row types
   */
  public getSelectorAggregationsForEntry(entry: QueryMetricEntry): Array<Section<SelectorTypeAggregation>> {
    const useNewMetricsMode = this.helpers.useNewMetricsMode();

    // old metrics mode displays operators available for the given selected type
    const listTypes = useNewMetricsMode ? this.getAddListTypeItems() : {[entry.type]: {value: entry.type}};

    let isBehaviorSelectorType = false;

    const filters = mapValues(listTypes, (rowTypeOptions, rowType) => {
      let operators: MATH_TYPE[] = null;
      switch (rowType) {
        case ResourceType.Event:
          operators = this.attr(`event-aggregator-functions`) ?? null;
          break;
        case SelectorTypeDefinitions.EventPropertyAggregation:
        case SelectorTypeDefinitions.UserPropertyAggregation:
          operators = this.attr(`property-aggregator-functions`) ?? null;
          break;
      }

      // currently only distinguishing property between behaviorSelectorType and regular selector types is the includeDateRange field
      // listTypes currently only has either all behaviorSelectorType or no behaviorSelectorTypes
      isBehaviorSelectorType = isBehaviorSelectorType || rowTypeOptions?.includeDateRange;

      return operators;
    });

    // TODO(pablo): aggregations have added complexity since we have 3
    // different UIs in production, simpplify this further
    // if using the new metrics mode, return all metrics options
    if (useNewMetricsMode) {
      const sections = nestedSelectorTypeAggregationsSections(Object.keys(listTypes) as SelectorType[]);

      return this.filterSelectorTypeAggregationSections(filters, sections, isBehaviorSelectorType);
    }

    let aggregations: Array<SectionItem<SelectorTypeAggregation>> = FlattenSelectorTypeAggregations.map((item) => ({
      ...item,
      label: item.value.shortName ?? this.formatDataGroupLabel(item.label),
    })).filter(
      (item: SectionItem<SelectorTypeAggregation>) =>
        Boolean(item.value.operator) &&
        (item.value.selectorType !== SelectorTypeDefinitions.EventPropertyAggregation || !item.value.perUserOperator) &&
        item.value.operator !== PRIMATIVE_MATH_TYPES.HISTOGRAM,
    );

    aggregations = this.filterSelectorTypeAggregation(aggregations, filters, isBehaviorSelectorType);

    return aggregations.length > 1 ? [{items: aggregations}] : [];
  }

  /**
   * Returns the list of sub aggregations for a given entry
   */
  public getSelectorSubAggregationsForEntry(entry?: QueryMetricEntry) {
    const selectedAggregation = this.getSelectedAggregationForEntry(entry);

    return (selectedAggregation && selectedAggregation.value.subAggregations) || [];
  }

  /**
   * Checks if a given entry is not commited to the list
   */
  public isStagedEntry(index: number) {
    return index >= this.state.stagedRows.length;
  }

  /**
   * Retrieve an entry from the list.
   *
   * Return type is overridable because the type combinations can't be expressed
   * at this generic level.
   */
  public getEntry<T extends QuerySuperEntry>(index: number): T {
    return this.getEntries({appendPlaceholder: true})[index] as T;
  }

  public getRows(appendPlaceholder = false): EntryRow[] {
    const {stagedRow, stagedRows} = this.state;

    return [
      ...stagedRows,
      // placeholder row
      ...(appendPlaceholder && stagedRow ? [stagedRow] : []),
    ];
  }

  public getEntries({appendPlaceholder = false}: {appendPlaceholder?: boolean} = {}): QuerySuperEntry[] {
    return this.getRows(appendPlaceholder).map((row) => row.entry);
  }

  /** callback for `Add grouped event` line action */
  addOrConvertToGroupedEvent(index: number) {
    if (this.attr(`group-event-behavior`) === GroupEventBehaviors.Append) {
      this.updateEntries(Action.Add, {index: index + 1, entry: this.getPlaceHolderEventEntry(ResourceType.Event)});
    } else if (this.attr(`group-event-behavior`) === GroupEventBehaviors.Nest) {
      this.updateEntries(Action.Group, {
        index,
      });
    } else {
      throw new Error(`Somehow doing group stuff with no behavior defined: ${this.attr(`group-event-behavior`)}`);
    }
  }

  /**
   * Performs one of Action operations on the staged entries
   */
  public updateEntries(
    action: Action,
    {
      entry,
      index,
      trackingProps,
      queryReason,
    }: {
      entry?: QuerySuperEntry;
      index: number;
      trackingProps?: Obj;
      queryReason?: QueryReason;
    },
  ): UpdateStatus {
    let entries = this.getEntries();

    if (this.isStagedEntry(index) && action === Action.Edit) {
      // when a staged entry row is ready to be commited, we override
      // the action to commit the staged entry
      if (this.shouldCommitUpdate(action, entry, index)) {
        action = Action.Add;
      } else {
        this.update({
          stagedRow: this.createEntryRow(index, entry),
        });
        return {dispatchedUpdate: false};
      }
    } else if (entry && this.shouldCommitUpdate(action, entry, index)) {
      entry.persistedEntry = null;
    } else if (action === Action.Edit) {
      // for any action that results in changing from a commited entry to a staged one
      // we persist the commited state in persistedEntry so that all commited entries
      // are preserved when another change occurs
      const sourceEntry = this.getEntry(index);
      const isStagingChanges = this.isStagingEntryChanges(sourceEntry, entry);
      entry.persistedEntry = isStagingChanges ? sourceEntry : null;
    }

    switch (action) {
      case Action.Add:
        entries = [...entries, {...entry, entryKey: this.nextEntryKey++}];
        break;
      case Action.Clone:
        entry = cloneDeep(entries[index]);
        if (!isPropertyEntry(entry)) {
          entry.event = omit(entry.event, `renamed`) as EventItem | SavedCohortItem;
        }
        entries = insertAtIndex(entries, index + 1, {...entry, entryKey: this.nextEntryKey++});
        break;
      case Action.Ungroup:
      case Action.Edit:
      case Action.EditFlowSteps:
        entries = replaceByIndex(entries, index, {...entries[index], ...entry});
        break;
      case Action.Remove:
        entry = entries[index];
        entries = removeByIndex(entries, index);
        break;
      case Action.Group:
        entry = cloneDeep(entries[index]);
        entries = replaceByIndex(entries, index, {
          filters: [],
          type: SelectorTypeDefinitions.GroupedEvent,
          event: [entry, this.getPlaceHolderEventEntry(ResourceType.Event)],
        });
        break;
    }

    return this.commitAndTrackUpdate(entries, entry, {queryReason, action, index, trackingProps});
  }

  private shouldCommitUpdate(action: Action, entry: QuerySuperEntry, index: number): boolean {
    if (action === Action.Group) return false;
    if ([Action.Remove, Action.Clone, Action.Drag].includes(action)) return true;
    return entry && !this.createEntryRow(index, entry).isPlaceholder();
  }

  private commitAndTrackUpdate(
    entries: QuerySuperEntry[],
    entry: QuerySuperEntry,
    {
      index,
      queryReason = QueryReason.QbOtherUpdate,
      action,
      trackingProps,
    }: Partial<{index: number; queryReason: QueryReason; action: Action; trackingProps: Obj}>,
  ): UpdateStatus {
    const updateStatus: UpdateStatus = {dispatchedUpdate: false};

    this.update({
      stagedRows: entries.map((entry, index) => this.createEntryRow(index, entry)),
      // make sured the currently staged row index is refreshed
      ...(this.state.stagedRow ? {stagedRow: this.createEntryRow(entries.length, this.state.stagedRow.entry)} : {}),
    });

    // TODO(pablo): avoid managing placeholder entries at the report level
    // as a first iteration, the ideal behavior is reports should not query
    // for empty rows but this behavior has added complexity for formulas
    // in Insights, exclusion steps and attribution in Funnels, as a side effect
    // any staged changes from rows other than the commited change or the staged
    // row will be lost on the next update to the entries attribute
    if (this.shouldCommitUpdate(action, entry, index)) {
      const commitedEntries: QuerySuperEntry[] = entries.map((entry) => {
        const mappedType = findKeyByValue(PROPERTY_AGGREGATION_TYPE_OVERRIDES, entry.type);
        return {
          ...omit(entry.persistedEntry ?? entry, [`persistedEntry`, `entryKey`]),

          // TODO(pablo): remove this temporal type override once internal testing is complete
          type: mappedType ?? entry.type,
        } as QuerySuperEntry;
      });

      this.setActiveEntry(null, null);
      this.pendingQueryReason = maxQueryReason([this.pendingQueryReason, queryReason]);
      this.dispatchUpdate(new CustomEvent(`update`, {detail: {entries: commitedEntries, updatedEntry: entry}}));
      updateStatus.dispatchedUpdate = true;

      // abort current staged row when committing an entry
      if (action === Action.Add) {
        this.abortRowStage();
      }
    }

    // edit actions are tracked with more detailed elsewhere
    if (action !== Action.Edit || (entry && isPropertyEntry(entry))) {
      this.trackAction(index, entry, action, trackingProps);
    }

    return updateStatus;
  }

  public dispatchTrack(eventName: string, props: Obj): void {
    const properties = {
      ...props,
      ...this.attr(`track-properties`),
      featureFlags: Array.from(this.state.featureFlags),
    };
    emitTrackEvent(this, {eventName, props: properties, eventPrefix: null, cancelable: true});
  }

  // TODO(pablo): find a better way to abstract this
  public getQueryBlockConfig(type: SelectorType): Obj {
    switch (type) {
      case ResourceType.Event:
      case SelectorTypeDefinitions.FlowsEvent:
      case SelectorTypeDefinitions.EventPropertyAggregation:
      case SelectorTypeDefinitions.UserPropertyAggregation:
        return {
          type: type === SelectorTypeDefinitions.UserPropertyAggregation ? `properties` : `events`,
          'all-data-groups': this.attr(`data-groups-enabled`),
          'exclude-autotrack-events': this.attr(`exclude-autotrack-events`),
          'exclude-dimension-properties': this.attr(`exclude-dimension-properties`),
          'include-custom-properties': this.attr(`include-custom-properties`),
          'is-blocked-people': this.attr(`is-blocked-people`),
          'project-has-people': this.attr(`project-has-people`),
          'project-has-cohorts': this.attr(`project-has-cohorts`) && this.attr(`allow-cohort-filter`),
          'show-all-events': this.attr(`show-all-events`),
          'show-any-event': this.attr(`show-any-event`),
          'show-session-event': this.attr(`show-sessions`),
          'show-top-events': this.attr(`show-top-events`),
          'support-b2b-cohorts': this.attr(`support-b2b-cohorts`),
        };
      case ResourceType.Cohort:
        return {
          type: `properties`,
          'include-custom-properties': this.attr(`include-custom-properties`),
          'project-has-cohorts': true,
          'hide-events': true,
          'project-has-people': false,
          'support-b2b-cohorts': this.attr(`support-b2b-cohorts`),
          'inline-cohorts-override': this.attr(`inline-cohorts-override`),
        };
      case ResourceType.User:
        return {
          type: `properties`,
          'exclude-dimension-properties': this.attr(`exclude-dimension-properties`),
          'include-custom-properties': this.attr(`include-custom-properties`),
          'project-has-cohorts': false,
          'hide-events': true,
          'project-has-people': true,
        };
      case SelectorTypeDefinitions.Filter:
      case SelectorTypeDefinitions.Segment: {
        let config: Obj = {
          'all-data-groups': this.attr(`data-groups-enabled`),
          'event-names': this.attr(`event-names`),
          'include-custom-properties': this.attr(`include-custom-properties`),
          'project-has-cohorts': this.attr(`project-has-cohorts`),
          'hide-events': this.attr(`hide-events`),
          'project-has-people': this.attr(`project-has-people`),
          'support-b2b-cohorts': this.attr(`support-b2b-cohorts`),
        };
        if (type === SelectorTypeDefinitions.Filter) {
          config = {
            ...config,
            type: `filter`,
          };
        } else {
          config = {
            ...config,
            type: `segment`,
            'other-group-by-options': this.attr(`other-group-by-options`),
          };
        }
        return config;
      }
      case SelectorTypeDefinitions.Behavior:
        return {
          type: `events`,
          'include-custom-properties': this.attr(`include-custom-properties`),
          'hide-events': false,
          'project-has-people': true,
          'show-all-events': this.attr(`show-all-events`),
          'show-any-event': this.attr(`show-any-event`),
          'show-session-event': this.attr(`show-sessions`),
          'show-top-events': this.attr(`show-top-events`),
        };
      case SelectorTypeDefinitions.EventLaunch:
        return {
          type: `events`,
          'exclude-autotrack-events': this.attr(`exclude-autotrack-events`),
          'include-custom-properties': this.attr(`include-custom-properties`),
          'is-blocked-people': this.attr(`is-blocked-people`),
          'project-has-people': this.attr(`project-has-people`),
          'show-all-events': this.attr(`show-all-events`),
          'show-any-event': this.attr(`show-any-event`),
          'show-session-event': this.attr(`show-sessions`),
          'show-top-events': this.attr(`show-top-events`),
        };
    }

    return {};
  }

  private _dispatchUpdate(ev) {
    this.dispatchEvent(ev);
  }

  private abortRowStage() {
    this.setActiveEntry(null, null);
    this.update({stagedRow: null});
    this.dispatchEvent(new CustomEvent(`abortStage`));
  }

  setActiveEntry(index: number, part: EntryPart) {
    this.update({
      activeEntry: index,
      entryPart: part,
    });
  }

  private isTypeOptionComplex(option: any): option is RowTypeComplexOption {
    return isObject(option);
  }

  private isSelectorTypeAggregation(type: any): type is SelectorTypeAggregation {
    return type && Object.prototype.hasOwnProperty.call(type, `selectorType`);
  }

  /**
   * Generates the type of items that can be added to the list
   */
  private getAddListTypeItems({expandedView = true} = {}): TypeList {
    const sections = {};
    const types = this.attr(`row-types`);

    // populate selected items
    for (const type of types) {
      let selectorType = this.isTypeOptionComplex(type) ? type.value : type;

      // TODO(pablo): remove this temporal type override once internal testing is complete
      selectorType = expandedView
        ? selectorType
        : findKeyByValue(PROPERTY_AGGREGATION_TYPE_OVERRIDES, selectorType) ?? selectorType;

      if (this.isTypeOptionComplex(type)) {
        sections[selectorType] = {
          ...LIST_TYPES[selectorType],
          ...type,
          value: selectorType,
        };
      } else {
        sections[selectorType] = LIST_TYPES[selectorType] ?? {value: selectorType};
      }
    }

    const transforms = {
      [ResourceType.Event]: (item) => {
        return {
          ...item,
          ...(this.attr(`add-entry-label`) ? {label: this.attr(`add-entry-label`)} : {}),
        };
      },
    };

    return mapValues(sections, (value, key) => (transforms[key] ? transforms[key](value) : value));
  }

  private getTrackingEvent(type: ResourceTrackingTypes, action: Action): string {
    const forExclusionStep = this.attr(`entry-type`) === EntryType.ExclusionEvent;

    if (forExclusionStep) {
      type = EntryType.ExclusionEvent;
    }

    return ResourceTrackingEvent[type][action];
  }

  /**
   * Updates aggregation function on an entry
   */
  private onEntryAggregationFunctionSelected(
    index: number,
    aggregation: SelectorTypeAggregation,
    isSubAggregation: boolean,
  ) {
    const type = aggregation.selectorType;

    const sourceEntry = this.getEntry<QueryMetricEntry>(index);

    // remove property selection when changing to a non-aggregation type
    const keepProperty = canPerformPropertyAggregation(type);

    // remove value selection when changing from any type to a cohort entry type
    const keepEvent = sourceEntry.type !== ResourceType.Cohort && type !== ResourceType.Cohort;

    // attemps to preserve the current aggregationOperator if it's available on the selected aggregation
    const keepAggregationOperator =
      !isSubAggregation && aggregation.subAggregations
        ? aggregation.subAggregations.some(
            (subAggregation) => subAggregation.value.operator === sourceEntry.aggregationOperator,
          )
        : false;

    const entry: QueryMetricEntry = {
      ...sourceEntry,
      type,
      property: keepProperty ? sourceEntry.property : null,
      event:
        keepEvent && sourceEntry.event.value
          ? sourceEntry.event
          : (this.getPlaceHolderEventEntry(type) as QueryEventEntry).event,
      aggregationOperator: keepAggregationOperator
        ? sourceEntry.aggregationOperator
        : this.getSelectorTypeDefaultAggregator(aggregation),
      aggregationOperatorPerUser: aggregation.perUserOperator || null,
    };

    this.updateEntries(Action.Edit, {entry, index});

    // focus on numeric property field when selecting a numeric property aggregation
    if (!!entry.event?.value && !isSubAggregation) {
      this.update({
        entryPart: EntryPart.NumericProperty,
        activeEntry: index,
      });
    }

    this.dispatchTrack(TrackingEvent.SelectNumericAggregator, {
      ...this.getEntryTrackingProps(index, entry),
      Aggregator: entry.aggregationOperator,
      'Per-user aggregator': entry.aggregationOperatorPerUser,
    });
  }

  isStagingEntryChanges(entry: QuerySuperEntry, updatedEntry: QuerySuperEntry) {
    const entryRow = this.createEntryRow(0, entry);
    const updatedEntryRow = this.createEntryRow(0, updatedEntry);

    return !entryRow.isPlaceholder() && updatedEntryRow.isPlaceholder();
  }

  /**
   * Updates event selection on an entry
   */
  onEntryEventSelected(
    index: number,
    value: EntryItem,
    {trackingProps, queryReason}: {trackingProps?: Obj; queryReason?: QueryReason},
  ) {
    const sourceEntry = this.getEntry(index);
    const event = isPropertyEntry(sourceEntry) ? value : pick(value, EVENT_PROPERTIES);
    let entry = {...sourceEntry, event} as QuerySuperEntry;
    const row = this.createEntryRow(index, entry);

    const isPlaceholder = row.isPlaceholder();
    let action;
    if (this.isStagedEntry(index) && !isPlaceholder) {
      action = Action.Add;
    } else if (isSegmentEntry(entry) && isEmptySegmentSelection((value as unknown) as Segment[])) {
      action = Action.Remove;
      entry = null;
    } else {
      action = Action.Edit;

      if (isSegmentEntry(entry) && entry.customBucket) {
        const existingProperty = this.getSegmentationPropertyByIndex(index);
        const updatedProperty = this.getSegmentationProperty(entry);
        if (
          updatedProperty.propertyType !== PropertyType.Number ||
          !isSamePropertyReference(existingProperty, updatedProperty)
        ) {
          entry.customBucket = null;
        }
      }
    }

    this.updateEntries(action, {entry, index, trackingProps, queryReason});

    this.update({
      entryPart: isPlaceholder ? EntryPart.NumericProperty : null,
    });
  }

  /**
   * Updates property aggregation selection on an entry
   */
  onEntryPropertySelected(index: number, property: PropertyItem) {
    const useNewMetricsMode = this.helpers.useNewMetricsMode();

    if (!property) {
      return this.onEntryPropertyRemoved(index);
    }

    const sourceEntry = this.getEntry<QueryMetricEntry>(index);
    let entry: QueryEntry = {
      ...sourceEntry,
      type: PROPERTY_AGGREGATION_TYPE_OVERRIDES[sourceEntry.type] ?? sourceEntry.type,
      property,
    };

    let aggregationOperator = entry.aggregationOperator;

    if (!useNewMetricsMode) {
      const entryAggregatorFunctions = this.getSelectorAggregationsForEntry(entry);
      const validPropertyAggregator = entryAggregatorFunctions.some((section) =>
        section.items.find((item) => item.value.operator === sourceEntry.aggregationOperator),
      );

      if (!validPropertyAggregator) {
        aggregationOperator = this.getEntryDefaultOperator(entry.type);
      }
    }

    entry = {
      ...entry,
      aggregationOperator,
      aggregationOperatorPerUser: useNewMetricsMode ? entry.aggregationOperatorPerUser : null,
    };

    this.updateEntries(Action.Edit, {entry, index});

    if (Action.Edit) {
      this.dispatchTrack(TrackingEvent.SelectNumericProperty, {
        ...this.getEntryTrackingProps(index, entry),
        'Property name': entry.property.label,
        'Property data type': entry.property.type,
      });
    }
  }

  /**
   * Handles the case where a custom property entity is removed and the given entry
   * is a property aggregation type, convert it to a total count since the property
   * is no longer in a valid state
   */
  onEntryPropertyRemoved(index: number) {
    const sourceEntry = this.getEntry<QueryMetricEntry>(index);
    const useNewMetricsMode = this.helpers.useNewMetricsMode();

    const entry: QueryEntry = {
      ...sourceEntry,
      aggregationOperator: this.getEntryDefaultOperator(sourceEntry.type),
      property: null,
      type: useNewMetricsMode
        ? sourceEntry.type
        : findKeyByValue(PROPERTY_AGGREGATION_TYPE_OVERRIDES, sourceEntry.type),
    };

    this.updateEntries(Action.Edit, {entry, index});
    this.dispatchTrack(TrackingEvent.ClearNumericProperty, {
      ...this.getEntryTrackingProps(index, entry),
    });
  }

  /**
   * Formats a given label to include the selected data group (Users, Accounts, etc...)
   */
  private formatDataGroupLabel(label: SelectorTypeAggregationLabel) {
    if (typeof label === `function`) {
      const replacements: {[key in AggregationGroupLabel]: string} = {
        [AggregationGroupLabel.Group]: this.getSelectedGroupLabel(),
        [AggregationGroupLabel.PluralGroup]: this.getSelectedGroupLabel(true),
        [AggregationGroupLabel.PluralGroupTitle]: toTitleCase(this.getSelectedGroupLabel(true)),
      };

      return label(replacements);
    }

    return label;
  }

  private getSelectedGroupLabel(isPlural = false) {
    const dataGroupId = this.attr(`data-group-id`);
    if (!dataGroupId) {
      return pluralize(`User`, isPlural);
    } else {
      const dataGroup = this.state.dataGroups.find((group) => group.groupID === dataGroupId);
      if (dataGroup) {
        return pluralize(dataGroup.displayName, isPlural, null, `(s)`);
      }

      return pluralize(`Group`, isPlural);
    }
  }

  private async fetchDataGroups() {
    const projectId = this.attr(`project-id`);
    const workspaceId = this.attr(`workspace-id`);
    const paramsUnchanged = () => projectId === this.attr(`project-id`) && workspaceId === this.attr(`workspace-id`);

    if (this.attr(`data-groups-enabled`)) {
      const dataGroups = await dataGroupsClient.query({projectId, workspaceId});
      if (paramsUnchanged()) {
        this.update({dataGroups: [DEFAULT_DATA_GROUP, ...dataGroups]});
      }
    }
  }

  /**
   * Stages or adds a new entry row for a give SelectorType or SelectorTypeAggregation
   */
  private addEntryRow(rowType: SelectorType | SelectorTypeAggregation) {
    const index = this.helpers.getEntries().length;
    const type = this.isSelectorTypeAggregation(rowType) ? rowType.selectorType : rowType;

    // checks for static defined row types and append that to the list
    const typeDefinition = this.getAddListTypeItems()[type];

    if (typeDefinition && typeDefinition.readOnlyEvent) {
      const entry = this.getNewEntry(type, {event: typeDefinition.readOnlyEvent});
      this.updateEntries(Action.Add, {entry, index});
    } else {
      this.update({
        stagedRow: this.createEntryRow(index, this.getNewEntry(type)),
        activeEntry: index,
        entryPart: EntryPart.Value,
      });
    }
  }

  private createEntryRow(index: number, entry: QuerySuperEntry) {
    return createEntryRow(entry.type, {index, entry, section: this}, {});
  }

  private getEntryLabel(entry: QuerySuperEntry): string {
    if (!entry) {
      return null;
    }

    if (entry.type === SelectorTypeDefinitions.Filter) {
      return renameProperty(entry.event.propertyName);
    } else if (entry.type === SelectorTypeDefinitions.Segment) {
      const segment = entry.event[0];
      if (isCohortSegment(segment)) {
        return segment[0][ResourceType.Cohort].name;
      } else if (isEventSegment(segment)) {
        return renameProperty(segment[ResourceType.Event].propertyName);
      } else if (isUserSegment(segment)) {
        return renameProperty(segment[ResourceType.User].propertyName);
      } else {
        return segment[ResourceType.Other].value;
      }
    } else if (entry.type === SelectorTypeDefinitions.GroupedEvent) {
      return `$grouped_event`;
    } else {
      return entry.event.label;
    }
  }

  /**
   * Prompts for a custom label on a given entry index
   */
  private async promptRename(index: number) {
    let entry = this.getEntry<QueryEventEntry>(index);
    const {event: stagedEvent} = entry;

    const inputValidator = (stagedInput) => {
      const trimmed = stagedInput.trim();
      const isValid = this.getEntries().every((entry: QueryEventEntry) => {
        return trimmed === stagedEvent.label || (trimmed !== entry.event.label && trimmed !== entry.event.renamed);
      });
      return isValid ? null : `Name is already used.`;
    };

    const promptResponse = await prompt(
      {
        title: `Rename`,
        'initial-input-value': stagedEvent.renamed || stagedEvent.label,
        'allow-empty-input-value': true,
        'confirm-button-label': `Save`,
      },
      {inputValidator},
    );

    if (promptResponse.didConfirm) {
      const newLabel = promptResponse.inputValue;
      const hasLabel = stagedEvent.label !== newLabel && newLabel !== ``;
      entry = {
        ...entry,
        event: {
          ...omit(stagedEvent, [`renamed`]),
          ...(hasLabel ? {renamed: newLabel} : {}),
        },
      };
      this.updateEntries(Action.Edit, {entry, index});
      this.dispatchTrack(TrackingEvent.RenameEvent, {
        ...this.getEntryTrackingProps(index, entry),
        'is custom event': entry.event?.custom,
        'Renamed name': newLabel,
        'is removing rename': !hasLabel,
      });
    }
  }

  getSegmentationProperty(entry: QuerySegmentEntry): Segmentation | null {
    const segment = entry.event[0];
    if (isCohortSegment(segment) || isOtherSegment(segment)) {
      return null;
    }

    return Object.values(segment)[0];
  }

  getSegmentationPropertyByIndex(index): Segmentation {
    return this.getSegmentationProperty(this.getEntry(index));
  }

  trackAction(index: number, entry: QuerySuperEntry, action: Action, trackingProps?: Obj) {
    this.dispatchTrack(this.getTrackingEvent(entry.type, action), {
      ...this.getEntryTrackingProps(index, entry),
      ...(trackingProps || {}),
      ...(entry && entry.type === ResourceType.Event ? {'is custom event': entry.event.custom} : {}),
      ...(entry.type === ResourceType.Cohort
        ? {
            'cohort type': isInlineCohortItem(entry.event) ? `inline cohorts` : `cohorts`,
          }
        : {}),
    });
  }

  getEntryTrackingProps(index: number, entryOverride?: QuerySuperEntry) {
    const entry = entryOverride || this.getEntry(index);
    return {
      'Entry index': index,
      'Entry name': this.getEntryLabel(entry),
      'Entry type': entry.type,
    };
  }

  getCustomBucketTrackingProps(bucket: Bucket) {
    return {
      'Bucket min': bucket.min,
      'Bucket max': bucket.max,
      'Bucket size': bucket.bucketSize,
    };
  }

  updateCustomBucket(index: number, customBucket: Bucket) {
    const entry = this.getEntry(index);
    const updatedEntry = {...entry, customBucket};
    this.updateEntries(Action.Edit, {entry: updatedEntry, index});
  }

  getCustomBucketByIndex(index: number): Bucket {
    return this.getEntry<QueryBucketableEntry>(index).customBucket;
  }

  canCustomizeBuckets(index: number) {
    const entry = this.getEntry(index);
    let result = false;
    if (entry.type === SelectorTypeDefinitions.Segment) {
      const property = this.getSegmentationPropertyByIndex(index);
      result = this.attr(`can-customize-numeric-buckets`) && property?.propertyType === PropertyType.Number;
    } else if (entry.aggregationOperator === PRIMATIVE_MATH_TYPES.HISTOGRAM) {
      result = this.attr(`can-customize-numeric-buckets`);
    }
    return result;
  }

  onCustomizeBuckets(index: number) {
    this.update({
      activeEntry: index,
      entryPart: EntryPart.CustomBucketing,
    });
    const customBucket = this.getCustomBucketByIndex(index);
    this.dispatchTrack(TrackingEvent.OpenCustomBucketPane, {
      ...this.getEntryTrackingProps(index),
      ...(customBucket ? this.getCustomBucketTrackingProps(customBucket) : {}),
      'Is editing': !!customBucket,
    });
  }

  /**
   * Applies a first time event filter as the first filter for a given entry
   */
  applyFirstTimeEventFilter(index: number) {
    const firstTimeEventFilter = createFirstTimeEventFilter();
    const entry: QuerySuperEntry = {
      ...this.getEntry(index),
      filters: [firstTimeEventFilter, ...(this.getEntry(index).filters ?? [])],
    };
    this.updateEntries(Action.Edit, {entry, index});
    this.trackAction(index, entry, Action.AddFilter, generateFilterTrackingProps(firstTimeEventFilter));
  }

  async onMapLookupTable(index: number) {
    const entry = this.getEntry(index);
    let segmentation;
    if (entry.type === SelectorTypeDefinitions.Segment) {
      segmentation = this.getSegmentationProperty(entry);
    } else {
      segmentation = entry.event;
    }
    const property = segmentationToPropertyItem(segmentation);

    const attrs: Omit<MapLookupTableModalAttrs, 'open'> = {
      'project-id': this.attr(`project-id`),
      'workspace-id': this.attr(`workspace-id`),
      'user-id': this.attr(`user-id`),
      'project-has-people': this.attr(`project-has-people`),
      'selected-property': (JSON.stringify(property) as unknown) as PropertyItemWithValue,
      'event-names': this.attr(`event-names`),
      'disable-property-selection': true,
      elref: `mapLookupTableModal`,
      type: `temporary-map`,
    };

    await promisifyModal({
      tagName: `mp-map-lookup-table-modal`,
      closeEvents: [`close`],
      attrs,
    });
  }

  canMapLookupTable(row: EntryRow): boolean {
    if (!featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.LookupTablesInlineMap)) {
      return false;
    }
    const {entry} = row;
    if (row.isPlaceholder() || !isPropertyEntry(entry)) {
      return false;
    }

    if (entry.type === SelectorTypeDefinitions.Filter) {
      const prop = transformFilterToPropertyItem(entry.event as PropertyFilter);
      return this.canMapPropToLookupTable(prop);
    } else if (isSegmentEntry(entry) && this.getSegmentationProperty(entry)) {
      const prop = segmentationToPropertyItem(this.getSegmentationProperty(entry));
      return this.canMapPropToLookupTable(prop);
    } else {
      return false;
    }
  }

  canMapPropToLookupTable(prop: Omit<PropertyItem, 'label'>): boolean {
    if (Array.isArray(prop.propertyName)) {
      return false;
    }
    if (getTempLookupTableMappings(prop.resourceType).has(prop.propertyName)) {
      return false;
    }
    return !propIneligibleForMappingReason(prop, true);
  }

  onUnmapLookupTable(index: number) {
    const entry = this.getEntry(index);
    let segmentation;
    if (entry.type === SelectorTypeDefinitions.Segment) {
      segmentation = this.getSegmentationProperty(entry);
    } else {
      segmentation = entry.event;
    }
    const property = segmentationToPropertyItem(segmentation);
    const propertyName = Array.isArray(property.value) ? property.value[0] : property.value;

    unregisterTempLookupTableMapping(property.resourceType, propertyName);
    if (entry.type === SelectorTypeDefinitions.Segment) {
      this.updateEntries(Action.Remove, {index});
    }
    addToast({
      message: `Removed temporary lookup table mapping`,
      type: ToastType.DESTRUCTIVE,
    });
  }

  canUnmapLookupTable(row: EntryRow): boolean {
    if (!featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.LookupTablesInlineMap)) {
      return false;
    }
    const {entry} = row;
    if (row.isPlaceholder() || !isPropertyEntry(entry)) {
      return false;
    }

    if (isFilterEntry(entry)) {
      return this.canUnmapPropFromLookupTable(entry.event);
    } else if (isSegmentEntry(entry) && this.getSegmentationProperty(entry)) {
      const prop = this.getSegmentationProperty(entry);
      return this.canUnmapPropFromLookupTable(prop);
    } else {
      return false;
    }
  }

  canUnmapPropFromLookupTable(prop: {resourceType?: ResourceType; propertyName?: string | string[]}) {
    if (!prop.resourceType || !prop.propertyName) {
      return false;
    }
    const name = Array.isArray(prop.propertyName) ? prop.propertyName[0] : prop.propertyName;
    return getTempLookupTableMappings(prop.resourceType).has(name);
  }
}

export const QueryEntrySectionEl = defineAndMakeJsxFactory<QueryEntrySectionAttrsJsxProps>(
  `mp-query-entry-section`,
  QueryEntrySection,
);
